import { useEffect, useRef } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db, auth } from "../firebase";
import { getSharedInfo, getJoinInfo } from "../utils/urlUtils";

/**
 * useSharedMode
 *
 * Escucha cambios en el hash de la URL (#/uid/lang/wishlistId o #/join/familyId/nombre)
 * y según lo que encuentre:
 *   - Modo compartido: carga los datos del usuario y su wishlist para mostrarlos
 *     sin que el visitante tenga que estar registrado.
 *   - Modo join: prepara el flujo de unirse a un grupo familiar.
 *
 * Si el usuario ya está autenticado cuando llega por un link compartido,
 * lo lleva directamente a ver el perfil dentro de la app.
 *
 * Uso en App.js:
 *   useSharedMode({ setSharedMode, setSharedLang, setSharedMember, ... });
 */
export function useSharedMode({
  setSharedMode,
  setSharedLang,
  setSharedMember,
  setSharedWishlist,
  setLoading,
  setJoinMode,
  setJoinFamilyId,
  setJoinFamilyName,
  setJoinScreen,
  setViewingMember,
  setViewingWishlist,
  setScreen,
  setActiveTab,
}) {
 
  const settersRef = useRef({});
  settersRef.current = {
    setSharedMode, setSharedLang, setSharedMember, setSharedWishlist,
    setLoading, setJoinMode, setJoinFamilyId, setJoinFamilyName, setJoinScreen,
    setViewingMember, setViewingWishlist, setScreen, setActiveTab,
  };

  useEffect(() => {
    async function procesarHash() {
      const {
        setSharedMode, setSharedLang, setSharedMember, setSharedWishlist,
        setLoading, setJoinMode, setJoinFamilyId, setJoinFamilyName, setJoinScreen,
        setViewingMember, setViewingWishlist, setScreen, setActiveTab,
      } = settersRef.current;
      const shared = getSharedInfo(); // lee /#/uid/lang/wishlistId
      const join = getJoinInfo();    // lee /#/join/familyId/nombre

      // ── CASO 1: Link de wishlist compartida ──────────────────────────────
      if (shared) {
        setSharedMode(true);
        setSharedLang(shared.lang);

        // Cargamos los datos del dueño de la wishlist
        const userSnap = await getDoc(doc(db, "users", shared.uid));
        if (!userSnap.exists()) {
          setLoading(false);
          return;
        }

        const userData = userSnap.data();
        setSharedMember({ id: shared.uid, ...userData });

        // Cargamos su wishlist (la indicada en la URL, o la primera disponible)
        if (userData.wishlists?.length > 0) {
          const targetId =
            shared.wishlistId && userData.wishlists.includes(shared.wishlistId)
              ? shared.wishlistId
              : userData.wishlists[0];

          const wSnap = await getDoc(doc(db, "wishlists", targetId));
          if (wSnap.exists()) {
            setSharedWishlist({ id: wSnap.id, ...wSnap.data() });
          }
        }

        setLoading(false);

        // Si el visitante ya tiene sesión iniciada, saltamos el modo compartido
        // y lo llevamos directamente al perfil dentro de la app
        const currentUser = auth.currentUser;
        if (currentUser) {
          const targetId =
            shared.wishlistId && userData.wishlists?.includes(shared.wishlistId)
              ? shared.wishlistId
              : userData.wishlists?.[0];

          if (targetId) {
            const wSnap2 = await getDoc(doc(db, "wishlists", targetId));
            if (wSnap2.exists()) {
              setViewingMember({ id: shared.uid, ...userData });
              setViewingWishlist({ id: wSnap2.id, ...wSnap2.data() });
              setScreen("profile");
              setActiveTab("wishlu");
              setSharedMode(false);
              // Limpiamos el hash para que no vuelva a dispararse
              window.history.replaceState(null, "", window.location.pathname);
            }
          }
        }
        return;
      }

      // ── CASO 2: Link de invitación a un grupo ────────────────────────────
      if (join) {
        setJoinMode(true);
        setJoinFamilyId(join.familyId);
        setJoinScreen("welcome");

        // El nombre puede venir en la URL o hay que buscarlo en Firestore
        if (join.familyName) {
          setJoinFamilyName(join.familyName);
        } else {
          const fSnap = await getDoc(doc(db, "families", join.familyId));
          if (fSnap.exists()) {
            setJoinFamilyName(fSnap.data().name || "");
          }
        }
      }
    }

    // Ejecutamos al montar el componente
    procesarHash();

    // Y también cada vez que el usuario cambia el hash manualmente
    window.addEventListener("hashchange", procesarHash);
    return () => window.removeEventListener("hashchange", procesarHash);
  }, []); // Solo se registra una vez: el listener de hashchange cubre el resto
}
